import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { HttpClient } from "@angular/common/http";
import { EndpointService } from "@app/core";
import { ToastrService } from "ngx-toastr";
import { SeriePredeterminada, Empresa, Sede } from "@app/shared";

export interface Subserie {
  id?: number;
  nombre?: string;
  codigo?: string;
}

export interface Serie {
  id?: number;
  nombre?: string;
  codigo?: string;
}

export interface Documental {
  id?: number;
  nombre?: string;
  codigo?: string;
  subserie?: Subserie;
  serie?: Serie;
  expediente?: boolean;
}

export interface Consecutive {
  id?: number;
  dependencia?: Dependencia; // Valor anexado a la tabla de tipo Int
  consecutivo: number;
  periodicidad: number;
  tipo_periodicidad: string;
  fecha_reinicio: Date;
  reinicio: boolean;
  detalle?: Detalles[];
}

export interface Dependencia {
  id: number;
  dependencia: string;
}

export interface Detalles {
  id: number;
  id_serie: number;
  serie: string;
  id_subserie: string;
  subserie: string;
  id_tipo_documental: number;
  tipo_documental: string;
}

@Injectable()
export class ConsecutiveManagementDataService {
  constructor(private http: HttpClient, private api: EndpointService, private toastr: ToastrService) {}

  /**
   * Consultar todos los tipos documentales
   *
   * @returns {Observable<Documental[]>}
   * @memberof ConsecutiveManagementDatService
   */
  getTypeDocumentary(): Observable<Documental[]> {
    return new Observable((obs) => {
      this.http.get<Documental[]>(this.api.get("adminTypeDocument")).subscribe(
        (typeDocumentary) => {
          obs.next(typeDocumentary);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   *Obtiene los consecutivos
   *
   * @return {*}  {Observable<Consecutive[]>}
   * @memberof ConsecutiveManagementDataService
   */
  getConsecutives(): Observable<Consecutive[]> {
    return new Observable((obs) => {
      this.http.get<Consecutive[]>(this.api.get("consecutives")).subscribe(
        (consecutive) => {
          obs.next(consecutive);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   *
   *
   * @param {Consecutive} consecutive
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  addConsecutive(consecutive: Consecutive): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<boolean>(this.api.get("consecutives"), consecutive).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Consecutivo agregado correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Actualiza un consecutivo
   *
   * @param {Consecutive} consecutive
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  updtaeConsecutive(id: number, consecutive: Consecutive): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.put<boolean>(this.api.get("consecutives", [id]), consecutive).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Consecutivo agregado correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Elimina un consecutivo del sistema
   *
   * @param {number} id
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  deleteConsecutive(id: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete<boolean>(this.api.get("consecutives", [id])).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Consecutivo eliminado correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Obtiene los detalles del consecutivo
   *
   * @param {number} id
   * @return {*}  {Observable<Consecutive>}
   * @memberof ConsecutiveManagementDataService
   */
  getConsecutiveDetails(id: number): Observable<Consecutive> {
    return new Observable((obs) => {
      this.http.get<Consecutive>(this.api.get("consecutives", [id])).subscribe(
        (consecutive) => {
          obs.next(consecutive);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   *Obtiene toda las series
   *
   * @return {*}  {Observable<SeriePredeterminada[]>}
   * @memberof ConsecutiveManagementDataService
   */
  getSeriesDocumentary(): Observable<SeriePredeterminada[]> {
    return new Observable(obs => {
      this.http.get<SeriePredeterminada[]>(this.api.get('adminSerie')).subscribe(
        seriesDocumentary => {
        obs.next(seriesDocumentary);
      },
      err => {
        obs.error(err);
      });
    });
  }

  /**
   *Agrega detalles a un consecutivo
   *
   * @param {*} detail
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  addDetailConsecutive(detail: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<boolean>(this.api.get("add_details"), detail).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Detalle de consecutivo agregado correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   *Actualiza los detalles de un consecutivo
   *
   * @param {*} detail
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  updateDetailConsecutive(idRow: number, detail: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.put<boolean>(this.api.get("add_details", [idRow]), detail).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Consecutivo actualizado correctamente");
          resolve(true);
        },
        (err) => {
          this.toastr.error("Operación fallida", "No se pudo actualizar el detalle");
        }
      );
    });
  }

  /**
   *Elimina el detalle de un consecutivo
   *
   * @param {number} id
   * @return {*}  {Promise<boolean>}
   * @memberof ConsecutiveManagementDataService
   */
  deleteDetails(id: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete<boolean>(this.api.get("add_details", [id])).subscribe(
        (_resp) => {
          this.toastr.success("Operación exitosa", "Detalle eliminado correctamente");
          resolve(true);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }


    /**
  *Consulta las empresas del sistema
  *
  * @return {*}  {Observable<Empresa[]>}
  * @memberof ConsecutiveManagementDataService
  */
  getCompanies(): Observable<Empresa[]> {
    return new Observable(obs => {
      this.http.get<Empresa[]>(this.api.get('company')).subscribe(
        companies => {
          obs.next(companies);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }


    /**
  *Consulta las sedes de una empresa
  *
  * @param {number} idCompany
  * @return {*}  {Observable<Sede[]>}
  * @memberof ConsecutiveManagementDataService
  */
  getSedes(idCompany: number): Observable<Sede[]> {
    return new Observable(obs => {
      this.http.get<Sede[]>(this.api.get('sedesManagement', [idCompany])).subscribe(
        sedes => {
          obs.next(sedes);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/documents/consecutive-management/shared/consecutive-management-component.data.service.ts